import React, { useEffect, useId, useState } from 'react';

import { Silence } from "@energy-advice/alertfan-api-client";
import { SilenceState } from "@energy-advice/alertfan-api-client/dist/lib/generated/api";
import { useDeleteSilence, useGetSilences } from "api/api-hooks/alert-fan-hooks";
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Tooltip } from "react-tooltip";
import { dateToReadableString } from "utils/time-utils";
import { stringArrayToTags } from "../../basic/strings-array-to-tags/StringArrayToTags";
import { copyToClipBoard } from '../dataset-info-by-id/utils';
import SilenceCreate from "./SilenceCreate";

const SilenceStateIconMap: Record<SilenceState, any> = {
    ACTIVE: <i className="pi pi-check-circle" style={{color: "green"}} />,
    EXPIRED: <i className="pi pi-clock" style={{color: "indianred"}} />,
    PENDING: <i className="pi pi-hourglass pi-spin" style={{color: "darkorange"}} />
}

const SilenceStateTextMap: Record<SilenceState, any> = {
    ACTIVE: "Active",
    EXPIRED: "Expired",
    PENDING: "Pending",
}

type SilencesType = {
    tags?: { topics?: Array<string>, items?: Array<string> },
    setTags: (tags: { topics?: Array<string>, items?: Array<string> }) => void
}

const Silences = ({tags, setTags}: SilencesType) => {

    const [addSilenceDialog, setAddSilenceDialog] = useState(false)
    const [showExpired, setShowExpired] = useState(false)

    const {data, error, refetch, isLoading} = useGetSilences(showExpired)

    useEffect(() => {
        if (tags && (tags.items || tags.topics)) {
            setAddSilenceDialog(true)
        }
    }, [tags])

    const showDialogFalse = () => {
        setAddSilenceDialog(false)
        setTags({})
    }

    const deleteSilenceBody = (rowData: Silence) => {
        const {mutate} = useDeleteSilence(showExpired, {
            onSuccess: () => {
                easasAlert({
                  summary: "Success",
                  severity: "success",
                  detail: "Silence was deleted",
                  life: 3000,
                });
            },
            onFailure: (err) => {
                easasAlert({ summary: "Error", severity: "error", detail: err, life: 3000 });
            }
        })
        const acceptFunc = () => {
            mutate(rowData.id)
        }

        const confirm = (event: any) => {
            confirmPopup({
                target: event.currentTarget,
                message: 'Are you sure you want to proceed?',
                icon: 'pi pi-exclamation-triangle',
                accept: () => acceptFunc()
            });
        }

        return <>
            <Button className="p-button-danger p-button-sm" icon="pi pi-trash" onClick={confirm}/>
            <ConfirmPopup/>
        </>
    }

    const idBody = (rowData: Silence) => {
        return <>
            <div id={rowData.id}
                 className="flex align-items-center justify-content-center"
                 onClick={async () => {
                     await copyToClipBoard(rowData.id)
                     easasAlert({
                       summary: "Success",
                       severity: "success",
                       detail: "ID Copied to clipboard",
                     });
                 }}
            >
                <i className="pi pi-info-circle" />
            </div>
            <Tooltip anchorSelect={`#${rowData.id}`} content={rowData.id} delayShow={300}/>
        </>
    }

    const stateBody = (rowData: Silence) => {
        const tooltipId = useId().replace(/[^a-zA-Z0-9]/g, "")

        return <>
            <div id={tooltipId} className="flex align-items-center justify-content-center">
                {SilenceStateIconMap[rowData.state]}
            </div>
            <Tooltip anchorSelect={`#${tooltipId}`} content={SilenceStateTextMap[rowData.state]} delayShow={300}/>
        </>
    }

    if (error) {
        return <div>Something went wrong...</div>
    }

    return <div className="flex flex-column gap-2">
        <div className="flex gap-2 justify-content-between align-items-center">

            <Button icon="pi pi-plus"
                    label="Create"
                    className="p-button-success p-button-sm"
                    onClick={() => setAddSilenceDialog(true)}
            />
            <div className="flex gap-2">
                <div className="flex flex-column justify-content-center">
                    <b>Show Expired</b>
                    <div className="flex justify-content-center">
                        <InputSwitch checked={showExpired} onChange={(e) => setShowExpired(e.value)}/>
                    </div>
                </div>
                <Button icon="pi pi-refresh" onClick={() => refetch()}/>
            </div>
        </div>
        <DataTable value={data} loading={isLoading} size="small">
            <Column header="ID" field="id" body={idBody} style={{width: "0"}}/>
            <Column header="State" field="state" style={{width: "0", whiteSpace: "nowrap"}} body={stateBody}/>
            <Column header="Item Tags" field="itemTags" body={(e: Silence) => stringArrayToTags(e.itemTags)}/>
            <Column header="Topic Tags" field="topicTags" body={(e: Silence) => stringArrayToTags(e.topicTags)}/>
            <Column header="Started" field="startsAt" style={{width: "0", whiteSpace: "nowrap"}}
                    body={(e: Silence) => e.startsAt ? dateToReadableString(new Date(e.startsAt)) : '-'}/>
            <Column header="Ends At" field="endsAt" style={{width: "0", whiteSpace: "nowrap"}}
                    body={(e: Silence) => e.endsAt ? dateToReadableString(new Date(e.endsAt)) : '-'}/>
            <Column header="Updated" field="updatedAt" style={{width: "0", whiteSpace: "nowrap"}}
                    body={(e: Silence) => e.updatedAt ? dateToReadableString(new Date(e.updatedAt)) : '-'}/>
            <Column header="Creator" field="createdBy" style={{width: "0", whiteSpace: "nowrap"}}/>
            <Column header="Comment" field="comment"/>
            <Column body={deleteSilenceBody} style={{width: "0"}}/>
        </DataTable>
        {addSilenceDialog &&
            <SilenceCreate showExpired={showExpired}
                           dialog={addSilenceDialog}
                           showDialog={showDialogFalse}
                           tags={tags}/>
        }
    </div>

};

export default Silences;